import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Users,
  Target,
  TrendingUp,
  CheckCircle,
  Star,
  FileText,
  Search,
  Filter,
  Plus,
  ArrowRight,
  Clock,
  DollarSign,
  Globe,
  Zap,
  Shield,
  Award,
  BarChart3,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  Play,
  Download,
  Lightbulb,
  Cpu,
  Database,
  Cloud,
  Lock,
  Settings,
  PieChart,
  GraduationCap,
} from "lucide-react";
import CommonContactUs from "./CommonContactUs";

const ForJobSeekersPage = () => {
  const [activeTab, setActiveTab] = useState("solutions");

  const solutions = [
    {
      icon: <Briefcase className="w-8 h-8 text-blue-600" />,
      title: "Tech Training",
      description:
        "Upskill yourself with cutting-edge technical training programs to enhance your marketability.",
      features: [
        "High-volume hiring",
        "Multi-location support",
        "Custom workflows",
        "Advanced analytics",
      ],
      link: "/tech-training",
    },
    {
      icon: <Search className="w-8 h-8 text-green-600" />,
      title: "Job Matching",
      description:
        "Connecting skilled contractors with opportunities that match their expertise and preferred engagement models.",
      features: [
        "HRIS integration",
        "Automated workflows",
        "Performance management",
        "Employee self-service",
      ],
      link: "/job-matching",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              For Job Seekers
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-100">
              Discover opportunities that match your skills and career
              aspirations_
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Services for Job Seekers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              TechMind offers specialized services designed to help tech
              professionals find the perfect job opportunities that align with
              their skills, experience, and career goals. We're committed to
              helping you advance your career in the tech industry.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {solution.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {solution.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {solution.description}
                </p>
                {/* <ul className="space-y-3 mb-6">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul> */}
                <Link
                  to={solution.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg"
                >
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How We Help Job Seekers
            </h2>
            {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to ensure successful implementation and adoption
            </p> */}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Career Assessment",
                description:
                  "We start by understanding your skills, experience, career goals, and preferences to create a comprehensive profile.",
              },
              {
                step: "02",
                title: "Job Matching",
                description:
                  "Our advanced algorithms and expert recruiters identify opportunities that align with your profile and career aspirations.",
              },
              {
                step: "03",
                title: "Interview Preparation",
                description:
                  "We prepare you for interviews with targeted companies, providing insights into the company culture and potential questions.",
              },
              {
                step: "04",
                title: "Ongoing Support",
                description:
                  "We provide continuous support throughout your job search and career journey, including negotiation assistance and onboarding guidance.",
              },
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="flex justify-center gap-4 flex-wrap mb-8"
      >
        <Link to="/services">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gray-100 text-gray-800 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-flex items-center"
          >
            <span>Back to Services</span>
          </motion.button>
        </Link>
        <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-300"
          >
            <GraduationCap size={18} />
            <span>Contact Us</span>
          </motion.button>
        </Link>
      </motion.div>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default ForJobSeekersPage;
